import React, { useEffect, useState, useRef } from 'react';
import { Alert, AlertTitle, Backdrop, CircularProgress, Container, Divider, Fade, Grid, InputLabel, Link, Typography, } from '@mui/material';
import { styled, alpha, } from "@mui/material/styles";
import { Card, CardActions, CardContent } from '@mui/material';
import { getRandomStr, } from '../../src/helpers';
import { useRouter } from 'next/router';
import getPageMetaTags from '../../lib/meta/_getMetaTags';
import useAuth from '../../src/hook/auth';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import { TextField, MenuItem, Button, Box, } from '@mui/material';
import getIssueOptions from '../../lib/issue/_getIssueOptions';
import sendUserComms from '../../lib/issue/_sendUserComms';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import WavingHandRoundedIcon from '@mui/icons-material/WavingHandRounded';
import Logo from '../../src/components/_common/Logo';
import { LoadingButton } from '@mui/lab';
import { getFingerprintFromCookie } from '../../src/components/_common/fingerprint';

const Field = styled((props) => (
  <TextField InputProps={{ disableUnderline: true }} {...props} />
))(({ theme }) => ({
  '& .MuiFilledInput-root': {
    overflow: 'hidden',
    borderRadius: 8,
    backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#1A2027',
    border: '1px solid',
    borderColor: theme.palette.mode === 'light' ? '#E0E3E7' : '#2D3843',
    transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow']),
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&.Mui-focused': {
      backgroundColor: 'transparent',
      boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 2px`,
      borderColor: theme.palette.primary.main,
    },
    '&.MuiFilledInput-root.Mui-disabled': { backgroundColor: 'transparent', }
  },
}));

const card = (
  <React.Fragment>
    <CardContent sx={{ pb: 1, }}>
      <WavingHandRoundedIcon sx={{ fontSize: '2rem', color: '#333', }} />
      <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
        Looking to contact us?
      </Typography>
      <Typography variant="body2">
        We love to hear from our users.
      </Typography>
    </CardContent>
    <CardActions sx={{ justifyContent: "center" }}>
      <Button variant="text" sx={{ textTransform: 'none', }} component="a" href="/contact" endIcon={<NavigateNextRoundedIcon />}>Visit our contact page</Button>
    </CardActions>
  </React.Fragment>
);

const Report = (props) => {
  const {
    issues, pageLoading
  } = props

  const router = useRouter();
  const auth = useAuth();
  const { user } = auth || {}
  const { reloadUserInfo, email, accessToken } = user || {}
  const { createdAt } = reloadUserInfo || {}
  const bots = ["googlebot", "bingbot", "slurp", "duckduckbot", "yandexbot", "facebot", "applebot"]
  const isBot = typeof window === "undefined" ? false : bots.some(el => navigator.userAgent.toLowerCase().includes(el))
  const isPremium = isBot ? true : (reloadUserInfo?.customAttributes && (JSON.parse(reloadUserInfo?.customAttributes)?.userType === "PREMIUM" || JSON.parse(reloadUserInfo?.customAttributes)?.userType === "PROFESSIONAL") ? true : false);
  const [plan, setPlan] = useState("Premium");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    issue: '',
    description: '',
    browserType: '',
    osType: '',
    deviceType: '',
  });

  const [errors, setErrors] = useState({});
  const descriptionLimit = 777;
  const fieldRefs = {
    email: useRef(null),
    issue: useRef(null),
    description: useRef(null),
  };

  // const browsers = [
  //   'Chrome',
  //   'Firefox',
  //   'Safari',
  //   'Edge',
  //   'Opera',
  //   'Internet Explorer',
  //   'Brave',
  //   'Vivaldi',
  //   'Tor',
  //   'UC Browser',
  //   'Samsung Internet',
  //   'Yandex Browser',
  //   'Maxthon',
  //   'Puffin',
  //   'Epic Privacy Browser',
  //   'SeaMonkey',
  //   'Comodo Dragon',
  //   'Falkon',
  //   'Waterfox'
  // ];

  // const osTypes = [
  //   'Windows',
  //   'macOS',
  //   'Linux',
  //   'Android',
  //   'iOS',
  //   'Chrome OS',
  // ];

  // const devices = [
  //   'Desktop',
  //   'Mobile',
  //   'Tablet',
  // ];

  useEffect(() => {
    setFormData({
      ...formData,
      ["email"]: email,
    });
  }, [user])

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validate = () => {
    let tempErrors = {};
    tempErrors.email = formData.email ? '' : 'Email is required';
    tempErrors.issue = formData.issue ? '' : 'Issue type is required';
    tempErrors.description = formData.description ? '' : 'Description is required';
    tempErrors.description = formData.description.length > descriptionLimit ? 'Description cannot exceed 777 characters' : tempErrors.description;

    setErrors(tempErrors);
    return Object.values(tempErrors).every((x) => x === '');
  };

  // const handleSubmit = (e) => {
  //   e.preventDefault();
  //   if (validate()) {
  //     const { email, issue, description, browserType, osType, deviceType } = formData;
  //     const reportDetails = `Email: ${email}, Issue: ${issue}, Description: ${description}, Browser: ${browserType}, OS: ${osType}, Device: ${deviceType}`;
  //     console.log(reportDetails);
  //     // Add your form submission logic here
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (validate()) {
        const { email, issue, description, browserType, osType, deviceType } = formData;
        let uid;
        try {
          uid = getFingerprintFromCookie();
        } catch (err) {
          console.error("Error getting uid-fingerprint from fingerprint:", err);
        }
        const reportDetails = `Issue: ${issue}, Description: ${description}, uid: ${uid}`;
        const response = await sendUserComms(reportDetails, accessToken);
        setLoading(false);
        if (response.sendUserComms.status === 'Success') {
          setSuccess(true);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          setSuccess(false);
          if (alert('There was an error submitting your report. Please try again.')) {
            window.location.reload();
          }
        }
      } else {
        setLoading(false);
        const firstErrorField = Object.keys(errors).find((key) => errors[key]);
        if (firstErrorField) {
          fieldRefs[firstErrorField]?.current?.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } catch (error) {
      console.error('Error submitting report:', error);
      setLoading(false);
      setSuccess(false);
      if (alert('There was an error submitting your report. Please try again.')) {
        window.location.reload();
      }
    }
  };


  if (success && !loading) {
    return (
      <Fade in={success && !loading}>
        <div style={{ background: 'linear-gradient(180deg, #f7f8fa 90%, #fdfdfd)', transition: 'all 0.3s ease', }}> {/*  */}
          <Container maxWidth="md" sx={{ py: 3, '& a': { textDecoration: 'none', color: "#174ea6" }, }}>
            <Box component="div" sx={{ borderRadius: '8px', p: 5, my: 7, background: '#fff !important', boxShadow: 'rgba(0,0,0,0.3) 0 1px 3px' }}>
              <Logo color={"dark"} height={50} width={100} />
              <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#000', my: 2.5, }}>
                Thank you for submitting your issue.
              </Typography>
              <Typography variant="p" component="div" sx={{ fontSize: '1rem', fontWeight: 400, mb: 3, color: '#444', }}>
                {email ? `We will respond to you personally.` : ''} Please be assured that your message has been received and will be reviewed by the Grufity Team.
                If we need to follow up with you regarding your suggestions for improving Grufity, we will contact you directly.
                <br />
                <br />
                If you would like to track all your submitted reports or submissions, please <Link component="button" underline="hover" sx={{ color: '#174ea6', mb: .2 }} onClick={() => router.push("/report-issue/status")}>click here</Link>.
                <br />
                <br />
                We appreciate your help in making Grufity an excellent platform.
              </Typography>
              <Typography variant="p" sx={{ fontSize: '.9rem', fontWeight: 600, color: '#000', my: 2, }}>
                Best regards,
                <br />
                The Grufity Team
              </Typography>
            </Box>
            <Box component="div" sx={{ width: "100%", display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <Link component="button" underline="hover" sx={{ color: '#666', mr: 'auto' }} onClick={() => setSuccess(false)}>{`← go back`}</Link>
              <Link component="button" underline="hover" sx={{ color: '#666', ml: 'auto' }} onClick={() => router.push("/report-issue/status")}>{`track issues →`}</Link>
            </Box>
          </Container>
        </div>
      </Fade>)
  }

  return (<>
    <div style={{ transition: 'all 0.3s ease', }}> {/* background: 'linear-gradient(180deg, #f7f8fa 90%, #fdfdfd)', */}
      <Container maxWidth="lg" sx={{ pt: 5, pb: 27, '& a': { textDecoration: 'none', color: "#174ea6" } }}>
        <Grid container spacing={{ xs: 2, md: 7 }}>
          <Grid size={{ xs: 12, sm: 12, md: 8 }}>
            <Box>
              <ReportProblemRoundedIcon sx={{ fontSize: '3rem', color: '#333', }} />
              <Typography variant="h1" sx={{ fontSize: '2rem', fontWeight: 600, color: '#333', mb: 2, mt: 1 }}>Report Issue</Typography>
              <Typography variant="p" component="div" sx={{ fontSize: '1rem', fontWeight: 400, mb: 1, color: '#444' }}>
                Please use the form below to report any issues or bugs you are experiencing on our website.
                <br />
                We carefully review all feedback and will respond to each submission individually. By providing your email address, you consent to us contacting you to gain a better understanding of the issue you submitted.
              </Typography>
              <Typography variant="caption" component="div" sx={{ fontSize: '.8rem', fontWeight: 400, mb: 2, color: '#777' }}>
                * Indicates required field.
              </Typography>
              {!accessToken && !pageLoading &&
                <Alert severity="warning" sx={{ fontSize: ".94rem", mt: 3, '&.MuiAlert-standardWarning': { color: 'hsl(215, 15%, 12%)', backgroundColor: 'hsla(48, 100%, 96%, 0.5)', border: '1px solid hsla(36, 100%, 34%, 0.15)' }, borderRadius: "8px" }}>
                  Logging in is not required to file an issue. However, if you <Link href="/auth">log in</Link>, you can track the status of your report or file an appeal.
                </Alert>}
              <Box sx={{ maxWidth: 500, mt: 4, '& .MuiInputLabel-root': { zIndex: loading ? 0 : 1 }, '& .MuiTextField-root': { marginTop: '8px', marginBottom: '16px', } }}>
                <form onSubmit={handleSubmit}>
                  <Field
                    name="email"
                    label="Email*"
                    variant="filled"
                    fullWidth
                    disabled={email}
                    value={formData.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={errors.email}
                    margin="normal"
                  />
                  <Field
                    select
                    name="issue"
                    label="Issue Type*"
                    variant="filled"
                    fullWidth
                    value={formData.issue}
                    disabled={issues.length === 0}
                    onChange={handleChange}
                    error={!!errors.issue}
                    helperText={errors.issue}
                    margin="normal"
                    SelectProps={{
                      MenuProps: {
                        MenuListProps: {
                          style: {
                            maxHeight: 200,
                          },
                        },
                      },
                    }}
                  >
                    {issues.map((d) => (
                      <MenuItem key={getRandomStr(5)} value={d?.option}>
                        {d?.option}
                      </MenuItem>
                    ))}
                  </Field>
                  <Field
                    name="description"
                    label="Description*"
                    variant="filled"
                    fullWidth
                    multiline
                    rows={5}
                    placeholder={`Explain briefly what the problem is, including any steps to reproduce the issue, or what you were doing when the issue occurred.`}
                    value={formData.description}
                    onChange={handleChange}
                    // disabled={issues.length === 0 || !accessToken}
                    error={!!errors.description}
                    helperText={
                      <>
                        {errors.description}
                        <span style={{ color: formData.description.length > descriptionLimit ? 'red' : 'inherit' }}>
                          {` `}{formData.description.length}/{descriptionLimit}
                        </span>
                      </>
                    }
                    margin="normal"
                    inputRef={fieldRefs.description}
                    InputProps={{
                      style: {
                        color: formData.description.length > descriptionLimit ? 'red' : 'inherit',
                      },
                    }}
                    sx={{
                      '&:hover, .MuiFilledInput-root:before, .MuiFilledInput-root:after, .MuiFilledInput-root:hover:not(.Mui-disabled, .Mui-error):before': {
                        borderBottom: 0
                      },
                    }}
                  />
                  {/* <InputLabel htmlFor="browserType" sx={{ color: '#333' }}>What type of browser are you using?</InputLabel>
               <Field
                  select
                  name="browserType"
                  label="Browser Type"
                  variant="filled"
                  fullWidth
                  disabled={!accessToken}
                  value={formData.browserType}
                  onChange={handleChange}
                  margin="normal"
                >
                  {browsers.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Field>
                <InputLabel htmlFor="osType" sx={{ color: '#333' }}>What type of operating system are you using?</InputLabel>
                <Field
                  select
                  name="osType"
                  label="Operating System"
                  variant="filled"
                  fullWidth
                  disabled={!accessToken}
                  value={formData.osType}
                  onChange={handleChange}
                  margin="normal"
                >
                  {osTypes.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Field>
                <InputLabel htmlFor="deviceType" sx={{ color: '#333' }}>What type of device are you using?</InputLabel>
                <Field
                  select
                  name="deviceType"
                  label="Device Type"
                  variant="filled"
                  fullWidth
                  disabled={!accessToken}
                  value={formData.deviceType}
                  onChange={handleChange}
                  margin="normal"
                >
                  {devices.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Field> */}
                  {/* <Divider sx={{ my: 3 }} /> */}
                  <LoadingButton type="submit" variant="contained" size="large"
                    color="primary" sx={{ textTransform: 'none', fontWeight: 600, mt: 2 }}
                    disableElevation loading={loading}
                    endIcon={<NavigateNextRoundedIcon />} loadingPosition="end"
                  >
                    Submit Issue
                  </LoadingButton>
                </form>
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <Box sx={{ mt: { md: 0, sm: 12, xs: 12 } }}>
              <Divider sx={{ mt: 4, mb: 4, display: { xs: 'block', sm: 'block', md: 'none' } }} />
              <Card variant="outlined" sx={{ background: '#f7f8fa', textAlign: 'center', borderRadius: '8px', border: "1px solid #8888881f" }}>{card}</Card>
              <Divider sx={{ mt: 4, mb: 3 }} />
              <Button onClick={() => router.push("/report-issue/status")} endIcon={<NavigateNextRoundedIcon />} sx={{ textTransform: 'none', fontWeight: 600, background: '#f7f8fa', width: '100%', border: "1px solid #8888881f" }}>View all your reported issues</Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </div>
    <Backdrop
      open={loading || pageLoading}
      sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, flexDirection: 'column', color: '#fff', backdropFilter: 'blur(3px)' }}
    >
      {!pageLoading && loading &&
        <Typography variant="p" component="div" sx={{ fontSize: '1rem', fontWeight: 500, mb: 3, }}>
          Submitting your issue...
        </Typography>}
      <CircularProgress color="inherit" />
    </Backdrop>
  </>
  );
};

export async function getServerSideProps({ query }) {
  const { getIssueOptions: options } = (await getIssueOptions()) || {}
  const meta_tags = (await getPageMetaTags(`/report-issue`, false)) || {}
  const { getMetaTags } = meta_tags || {}
  return {
    props: {
      meta_tags: getMetaTags?.tags || null,
      issues: options || {},
    },
  }
}

export default Report;
